import config from '../../../config/api';
import type { ChartPeriod } from '../../../pages/dslAgentTest/chartPeriod';
import type {
  DslTag,
  TraceLineStyle,
  TraceLineWidth,
} from '../../../pages/dslAgentTest/types';
import type { WidgetPlacement } from '../../../components/grid-dashboard';
import {
  CHART_WIDGET_TYPE,
  EXCEL_WIDGET_TYPE,
  isCfRule,
  isExcelCellNumberFormat,
  type CfOp,
  type CfRule,
  type ChartWidgetConfig,
  type ExcelCell,
  type ExcelCellNumberFormat,
  type ExcelWidgetConfig,
} from '../widgets';

export interface DashboardSummary {
  id: string;
  name: string;
  updatedAt: string;
}

export interface MonitorDashboard {
  id: string;
  userId: string;
  name: string;
  docVersion: number;
  widgets: WidgetPlacement[];
  createdAt: string;
  updatedAt: string;
}

interface ApiTagDto {
  tag_id: string;
  dsl: string;
  color: string;
  line_style: TraceLineStyle;
  line_width: TraceLineWidth;
}

interface ApiChartWidgetDto {
  instance_id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  version?: number;
  pinned?: boolean;
  widget_type: 'chart';
  chart_id: string;
  title: string;
  period: ChartPeriod;
  tags: ApiTagDto[];
}

type ApiExcelCellNumberFormat =
  | { kind: 'percent' }
  | { kind: 'decimal'; digits: number }
  | { kind: 'text' }
  | { kind: 'date' };

interface ApiCfCondition {
  op: CfOp;
  target: number;
}

interface ApiCfRule {
  id: string;
  groups: ApiCfCondition[][];
  text_color?: string;
  bg_color?: string;
}

interface ApiExcelCellDto {
  r: number;
  c: number;
  raw?: string;
  resolved_dsl?: string;
  text_color?: string;
  bg_color?: string;
  number_format?: ApiExcelCellNumberFormat;
  conditional_formatting?: ApiCfRule[];
}

interface ApiExcelWidgetDto {
  instance_id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  version?: number;
  pinned?: boolean;
  widget_type: 'excel';
  sheet_id: string;
  rows: number;
  cols: number;
  cells: ApiExcelCellDto[];
}

type ApiWidgetDto = ApiChartWidgetDto | ApiExcelWidgetDto;

interface ApiDashboardDto {
  id: string;
  user_id: string;
  name: string;
  doc_version?: number;
  widgets: ApiWidgetDto[];
  created_at: string;
  updated_at: string;
}

interface ApiDashboardSummaryDto {
  id: string;
  name: string;
  updated_at: string;
}

const tagFromApi = (api: ApiTagDto): DslTag => ({
  id: api.tag_id,
  dsl: api.dsl,
  color: api.color,
  lineStyle: api.line_style,
  lineWidth: api.line_width,
});

const cfRuleFromApi = (api: ApiCfRule): CfRule => {
  const rule: CfRule = {
    id: api.id,
    groups: api.groups.map((g) => g.map((c) => ({ op: c.op, target: c.target }))),
  };
  if (api.text_color != null) rule.textColor = api.text_color;
  if (api.bg_color != null) rule.bgColor = api.bg_color;
  return rule;
};

const excelCellFromApi = (api: ApiExcelCellDto): ExcelCell => {
  const cell: ExcelCell = { raw: api.raw ?? '' };
  if (api.resolved_dsl != null) cell.resolvedDsl = api.resolved_dsl;
  if (api.text_color != null) cell.textColor = api.text_color;
  if (api.bg_color != null) cell.bgColor = api.bg_color;
  if (api.number_format != null && isExcelCellNumberFormat(api.number_format)) {
    cell.numberFormat = api.number_format as ExcelCellNumberFormat;
  }
  if (Array.isArray(api.conditional_formatting)) {
    const rules = api.conditional_formatting
      .map(cfRuleFromApi)
      .filter(isCfRule);
    if (rules.length > 0) cell.conditionalFormatting = rules;
  }
  return cell;
};

const buildEmptyExcelGrid = (rows: number, cols: number): ExcelCell[][] =>
  Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ raw: '' } as ExcelCell)),
  );

const cellsFromSparse = (
  rows: number,
  cols: number,
  sparse: ApiExcelCellDto[],
): ExcelCell[][] => {
  const grid = buildEmptyExcelGrid(rows, cols);
  for (const dto of sparse) {
    if (dto.r < 0 || dto.r >= rows || dto.c < 0 || dto.c >= cols) {
      throw new Error(
        `excel cell (${dto.r},${dto.c}) out of bounds (rows=${rows}, cols=${cols})`,
      );
    }
    grid[dto.r][dto.c] = excelCellFromApi(dto);
  }
  return grid;
};

const isDefaultCell = (cell: ExcelCell): boolean =>
  cell.raw === '' &&
  cell.resolvedDsl === undefined &&
  cell.textColor === undefined &&
  cell.bgColor === undefined &&
  cell.numberFormat === undefined &&
  (cell.conditionalFormatting === undefined ||
    cell.conditionalFormatting.length === 0);

const cfRuleToApi = (rule: CfRule): ApiCfRule => {
  const dto: ApiCfRule = {
    id: rule.id,
    groups: rule.groups.map((g) => g.map((c) => ({ op: c.op, target: c.target }))),
  };
  if (rule.textColor !== undefined) dto.text_color = rule.textColor;
  if (rule.bgColor !== undefined) dto.bg_color = rule.bgColor;
  return dto;
};

const cellsToSparse = (cells: ExcelCell[][]): ApiExcelCellDto[] => {
  const out: ApiExcelCellDto[] = [];
  for (let r = 0; r < cells.length; r++) {
    const row = cells[r];
    for (let c = 0; c < row.length; c++) {
      const cell = row[c];
      if (isDefaultCell(cell)) continue;
      const dto: ApiExcelCellDto = { r, c };
      if (cell.raw !== '') dto.raw = cell.raw;
      if (cell.resolvedDsl !== undefined) dto.resolved_dsl = cell.resolvedDsl;
      if (cell.textColor !== undefined) dto.text_color = cell.textColor;
      if (cell.bgColor !== undefined) dto.bg_color = cell.bgColor;
      if (cell.numberFormat !== undefined) dto.number_format = cell.numberFormat;
      if (cell.conditionalFormatting && cell.conditionalFormatting.length > 0) {
        dto.conditional_formatting = cell.conditionalFormatting.map(cfRuleToApi);
      }
      out.push(dto);
    }
  }
  return out;
};

const widgetFromApi = (api: ApiWidgetDto): WidgetPlacement => {
  if (api.widget_type === 'chart') {
    const config: ChartWidgetConfig = {
      chartId: api.chart_id,
      title: api.title,
      period: api.period,
      tags: api.tags.map(tagFromApi),
    };
    return {
      instanceId: api.instance_id,
      widgetType: CHART_WIDGET_TYPE,
      x: api.x,
      y: api.y,
      w: api.w,
      h: api.h,
      version: api.version ?? 0,
      pinned: api.pinned === true,
      config: config as unknown as Record<string, unknown>,
    };
  }
  if (api.widget_type === 'excel') {
    const config: ExcelWidgetConfig = {
      sheetId: api.sheet_id,
      rows: api.rows,
      cols: api.cols,
      cells: cellsFromSparse(api.rows, api.cols, api.cells),
    };
    return {
      instanceId: api.instance_id,
      widgetType: EXCEL_WIDGET_TYPE,
      x: api.x,
      y: api.y,
      w: api.w,
      h: api.h,
      version: api.version ?? 0,
      pinned: api.pinned === true,
      config: config as unknown as Record<string, unknown>,
    };
  }
  throw new Error(`unknown widget_type from API: ${(api as ApiWidgetDto).widget_type}`);
};

const dashboardFromApi = (api: ApiDashboardDto): MonitorDashboard => ({
  id: api.id,
  userId: api.user_id,
  name: api.name,
  docVersion: api.doc_version ?? 0,
  widgets: api.widgets.map(widgetFromApi),
  createdAt: api.created_at,
  updatedAt: api.updated_at,
});

const summaryFromApi = (api: ApiDashboardSummaryDto): DashboardSummary => ({
  id: api.id,
  name: api.name,
  updatedAt: api.updated_at,
});

const tagToApi = (tag: DslTag): ApiTagDto => ({
  tag_id: tag.id,
  dsl: tag.dsl,
  color: tag.color,
  line_style: tag.lineStyle,
  line_width: tag.lineWidth,
});

const widgetToApi = (widget: WidgetPlacement): ApiWidgetDto => {
  if (widget.widgetType === CHART_WIDGET_TYPE) {
    const cfg = (widget.config ?? {
      chartId: '',
      title: 'Chart',
      period: '3Y',
      tags: [],
    }) as unknown as ChartWidgetConfig;
    return {
      instance_id: widget.instanceId,
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
      version: widget.version ?? 0,
      pinned: widget.pinned === true,
      widget_type: 'chart',
      chart_id: cfg.chartId,
      title: cfg.title,
      period: cfg.period,
      tags: cfg.tags.map(tagToApi),
    };
  }
  if (widget.widgetType === EXCEL_WIDGET_TYPE) {
    const cfg = widget.config as unknown as ExcelWidgetConfig;
    return {
      instance_id: widget.instanceId,
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
      version: widget.version ?? 0,
      pinned: widget.pinned === true,
      widget_type: 'excel',
      sheet_id: cfg.sheetId,
      rows: cfg.rows,
      cols: cfg.cols,
      cells: cellsToSparse(cfg.cells),
    };
  }
  throw new Error(`unsupported widgetType: ${widget.widgetType}`);
};

export const widgetsToApi = (widgets: WidgetPlacement[]): ApiWidgetDto[] =>
  widgets.map(widgetToApi);

export const widgetsFromApi = (api: ApiWidgetDto[]): WidgetPlacement[] =>
  api.map(widgetFromApi);

const baseUrl = (): string => `${config.apiUrl}/market-monitor/dashboards`;

const buildHeaders = (userId: string, token: string | null): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-ID': userId,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handleResponse = async <T>(response: Response, label: string): Promise<T> => {
  const text = await response.text();
  if (!response.ok) {
    let message = `${label} failed with HTTP ${response.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text) as { detail?: string };
        message = typeof parsed.detail === 'string' ? parsed.detail : text;
      } catch {
        message = text;
      }
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  if (!text) return {} as T;
  return JSON.parse(text) as T;
};

export const listDashboards = async (
  userId: string,
  token: string | null,
): Promise<DashboardSummary[]> => {
  const response = await fetch(baseUrl(), { headers: buildHeaders(userId, token) });
  const body = await handleResponse<ApiDashboardSummaryDto[]>(response, 'List dashboards');
  return body.map(summaryFromApi);
};

export const getDashboard = async (
  userId: string,
  token: string | null,
  dashboardId: string,
): Promise<MonitorDashboard> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}`;
  const response = await fetch(url, { headers: buildHeaders(userId, token) });
  const body = await handleResponse<ApiDashboardDto>(response, 'Open dashboard');
  return dashboardFromApi(body);
};

export const createDashboard = async (
  userId: string,
  token: string | null,
  name: string,
  widgets: WidgetPlacement[],
): Promise<MonitorDashboard> => {
  const response = await fetch(baseUrl(), {
    method: 'POST',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({
      name,
      widgets: widgetsToApi(widgets),
    }),
  });
  const body = await handleResponse<ApiDashboardDto>(response, 'Create dashboard');
  return dashboardFromApi(body);
};

export const renameDashboard = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  expectedDocVersion: number,
  name: string,
): Promise<MonitorDashboard> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({ user_id: userId, expected_doc_version: expectedDocVersion, name }),
  });
  const body = await handleResponse<ApiDashboardDto>(response, 'Rename dashboard');
  return dashboardFromApi(body);
};

export const updateWidget = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  instanceId: string,
  expectedVersion: number,
  widget: WidgetPlacement,
): Promise<WidgetPlacement> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(instanceId)}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({
      user_id: userId,
      expected_version: expectedVersion,
      widget: widgetToApi(widget),
    }),
  });
  const body = await handleResponse<ApiWidgetDto>(response, 'Save widget');
  return widgetFromApi(body);
};

export const updateLayoutPositions = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  expectedDocVersion: number,
  positions: Array<{ instanceId: string; x: number; y: number; w: number; h: number }>,
): Promise<MonitorDashboard> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}/layout`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({
      user_id: userId,
      expected_doc_version: expectedDocVersion,
      positions: positions.map((p) => ({
        instance_id: p.instanceId,
        x: p.x,
        y: p.y,
        w: p.w,
        h: p.h,
      })),
    }),
  });
  const body = await handleResponse<ApiDashboardDto>(response, 'Save layout');
  return dashboardFromApi(body);
};

export const addWidget = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  expectedDocVersion: number,
  widget: WidgetPlacement,
): Promise<MonitorDashboard> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}/widgets`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({
      user_id: userId,
      expected_doc_version: expectedDocVersion,
      widget: widgetToApi(widget),
    }),
  });
  const body = await handleResponse<ApiDashboardDto>(response, 'Add widget');
  return dashboardFromApi(body);
};

export const removeWidget = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  instanceId: string,
  expectedDocVersion: number,
): Promise<MonitorDashboard> => {
  const params = new URLSearchParams({
    user_id: userId,
    expected_doc_version: String(expectedDocVersion),
  });
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(instanceId)}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: buildHeaders(userId, token),
  });
  const body = await handleResponse<ApiDashboardDto>(response, 'Remove widget');
  return dashboardFromApi(body);
};

export const deleteDashboard = async (
  userId: string,
  token: string | null,
  dashboardId: string,
): Promise<void> => {
  const url = `${baseUrl()}/${encodeURIComponent(dashboardId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: buildHeaders(userId, token),
  });
  await handleResponse<unknown>(response, 'Delete dashboard');
};
